import styled from 'styled-components';

import { colors } from 'constants/colors';

import { useHasPermission } from 'hooks/useHasPermission';

import {
    ColumnProductLightFragment,
    GetMicrostoreProductsQuery,
    GetSiteSetupQuery,
    MicrostoreColumnSetupState,
    MicrostoreColumnInventoryFragmentFragment,
    MicrostoreColumnSetupFragmentFragment,
    MicrostoreColumnType,
    MicrostoreColumnWithShelvesFragmentFragment,
    SetupInstructionType,
    TotadminPermission,
    useGetColumnProductsQuery,
} from 'data/__generated__';
import {
    isCheckBoxTemperatureInstruction,
    isCheckTemperatureInstruction,
    isHotDrinksInstruction,
    isManualReassortInstruction,
    isPhotosInstruction,
    isRemoveProductsInstruction,
    isStoreProductsInstruction,
} from 'pages/SiteSetups/utils/typePredicates';
import { ColumnInventorySection } from './inventory/ColumnInventorySection';
import { ColumnPhotosSection } from './photos/ColumnPhotosSection';
import { ColumnTemperatureSection } from './temperature/ColumnTemperatureSection';
import { DeliveryProductSummary, StoreProductsSection } from './storeProducts/StoreProductsSection';
import { ManualReassortSection } from './manualReassort/ManualReassortSection';
import { SETUP_INSTRUCTION_TYPE_LABELS } from 'pages/SiteSetups/constants/types';

// Variations sent per product by the API (column-product batch functions).
const LATEST_STOCK_VARIATIONS_LIMIT = 20;
// The setup's stock variations are written right after the site setup finishes (SiteSetup.finishAndSave).
// ponytail: a sale within this minute counts as part of the setup.
const SETUP_STOCK_UPDATE_WINDOW_MS = 60 * 1000;

// Current stock minus what moved since `time`. Null if all loaded variations are after it: some may be missing.
const getStockAt = (
    { stock, latestStockVariations }: ColumnProductLightFragment,
    time: number | null,
): number | null => {
    if (time === null) return stock;
    const variationsSince = latestStockVariations.filter(({ createdAt }) => createdAt > time);
    if (variationsSince.length >= LATEST_STOCK_VARIATIONS_LIMIT) return null;
    return stock - variationsSince.reduce((sum, { delta }) => sum + delta, 0);
};

export const ColumnDetails = ({
    deliveryProductSummary,
    inventoryDate,
    products,
    selectedColumn,
    selectedColumnInventory,
    selectedColumnSetup,
    site,
    siteSetupDate,
    siteSetupFinishedAt,
}: {
    deliveryProductSummary: DeliveryProductSummary;
    inventoryDate: string;
    products: GetMicrostoreProductsQuery['getMicrostoreProducts'];
    selectedColumn: MicrostoreColumnWithShelvesFragmentFragment;
    selectedColumnInventory: MicrostoreColumnInventoryFragmentFragment;
    selectedColumnSetup: MicrostoreColumnSetupFragmentFragment;
    site: GetSiteSetupQuery['getSiteSetup']['site'];
    siteSetupDate: string;
    siteSetupFinishedAt: number | null;
}) => {
    // Unsecured columns share the site's virtual stock, not reachable here.
    const canReadColumns = useHasPermission(TotadminPermission.MicrostoreColumnsRead);
    const { data: columnProductsData } = useGetColumnProductsQuery({
        variables: { columnId: selectedColumn._id, offset: 0, limit: 1000 },
        skip: !canReadColumns || selectedColumn.microstoreColumnType !== MicrostoreColumnType.SecuredMicrostore,
        fetchPolicy: 'network-only',
    });
    const columnProductById = new Map(
        (columnProductsData?.columnProducts.columnProducts ?? []).map((columnProduct) => [
            columnProduct.productId,
            columnProduct,
        ]),
    );
    // Out of stock is judged on the stock at setup time, not today's: a sale made since must not turn a spiral red.
    // - before the setup: when the deliverer started the column (or, if never started, when the site setup finished);
    // - after the setup: once the site setup finished and its stock variations were written.
    // Site setup not finished yet: no time, the current stock is used.
    const columnSetupStartedAt =
        selectedColumnSetup.stateHistory.find(({ state }) => state === MicrostoreColumnSetupState.InProgress)
            ?.createdAt ?? null;
    const beforeSetupTime = columnSetupStartedAt ?? siteSetupFinishedAt;
    const afterSetupTime = siteSetupFinishedAt === null ? null : siteSetupFinishedAt + SETUP_STOCK_UPDATE_WINDOW_MS;
    const setupLocationById = new Map(
        (selectedColumnSetup.microstoreLocations ?? []).map((setupLocation) => [
            setupLocation.microstoreLocationId,
            setupLocation,
        ]),
    );
    const locations = selectedColumn.shelves.flatMap(({ locationTable }) => locationTable.flat());
    // Products from the setup snapshot: the column plan may have changed since.
    const getOutOfStockLocationIds = (isBeforeSetup: boolean): Set<string> => {
        if (!columnProductsData) return new Set();
        return new Set(
            locations
                .filter(({ _id, productId }) => {
                    const setupLocation = setupLocationById.get(_id);
                    const locationProductId = setupLocation
                        ? isBeforeSetup
                            ? setupLocation.productId
                            : setupLocation.replacementProductId
                        : productId;
                    if (!locationProductId) return false;
                    const columnProduct = columnProductById.get(locationProductId);
                    if (!columnProduct) return true;
                    const stock = getStockAt(columnProduct, isBeforeSetup ? beforeSetupTime : afterSetupTime);
                    return stock !== null && stock <= 0;
                })
                .map(({ _id }) => _id),
        );
    };

    const temperatureInstruction = selectedColumnSetup.instructions.find(
        ({ type }) => type === SetupInstructionType.CheckTemperature,
    );
    const boxTemperatureInstruction = selectedColumnSetup.instructions.find(
        ({ type }) => type === SetupInstructionType.CheckBoxTemperature,
    );

    const removedProductsIntruction = selectedColumnSetup.instructions.find(
        ({ type }) => type === SetupInstructionType.RemoveProducts,
    );
    const photosInstruction = selectedColumnSetup.instructions.find(({ type }) => type === SetupInstructionType.Photos);
    const photosStartInstruction = selectedColumnSetup.instructions.find(
        ({ type }) => type === SetupInstructionType.PhotosStart,
    );
    const hotDrinksInstruction = selectedColumnSetup.instructions.find(
        ({ type }) => type === SetupInstructionType.HotDrinks,
    );
    const storeProductsInstruction = selectedColumnSetup.instructions.find(
        ({ type }) => type === SetupInstructionType.StoreProducts,
    );
    const manualReassortInstruction = selectedColumnSetup.instructions.find(
        ({ type }) => type === SetupInstructionType.ManualReassort,
    );

    return (
        <Container>
            {boxTemperatureInstruction && isCheckBoxTemperatureInstruction(boxTemperatureInstruction) ? (
                <ColumnTemperatureSection instruction={boxTemperatureInstruction} />
            ) : null}
            {temperatureInstruction && isCheckTemperatureInstruction(temperatureInstruction) ? (
                <ColumnTemperatureSection instruction={temperatureInstruction} />
            ) : null}

            <ColumnInventorySection
                column={selectedColumn}
                columnSetup={selectedColumnSetup}
                inventoryDate={inventoryDate}
                outOfStockLocationIds={getOutOfStockLocationIds(true)}
                products={products}
                columnInventory={selectedColumnInventory}
                site={site}
                removeProductsInstruction={
                    removedProductsIntruction && isRemoveProductsInstruction(removedProductsIntruction)
                        ? removedProductsIntruction
                        : undefined
                }
                hotDrinksInstruction={
                    hotDrinksInstruction && isHotDrinksInstruction(hotDrinksInstruction)
                        ? hotDrinksInstruction
                        : undefined
                }
                siteSetupDate={siteSetupDate}
            />
            {storeProductsInstruction && isStoreProductsInstruction(storeProductsInstruction) ? (
                <StoreProductsSection
                    column={selectedColumn}
                    columnSetup={selectedColumnSetup}
                    deliveryProductSummary={deliveryProductSummary}
                    outOfStockLocationIds={getOutOfStockLocationIds(false)}
                    products={products}
                    site={site}
                    siteSetupDate={siteSetupDate}
                />
            ) : null}
            {manualReassortInstruction && isManualReassortInstruction(manualReassortInstruction) ? (
                <ManualReassortSection instruction={manualReassortInstruction} products={products} />
            ) : null}
            {photosStartInstruction && isPhotosInstruction(photosStartInstruction) ? (
                <ColumnPhotosSection
                    instruction={photosStartInstruction}
                    title={SETUP_INSTRUCTION_TYPE_LABELS[photosStartInstruction.type]}
                />
            ) : null}
            {photosInstruction && isPhotosInstruction(photosInstruction) ? (
                <ColumnPhotosSection
                    instruction={photosInstruction}
                    title={SETUP_INSTRUCTION_TYPE_LABELS[photosInstruction.type]}
                />
            ) : null}
        </Container>
    );
};

const Container = styled.div`
    border: 1px solid ${colors.pureBlack};
    border-top: 0px;
    border-radius: 0 0 10px 10px;
    padding: 10px;

    & > :not(:first-child) {
        margin-top: 20px;
        border-top: 1px solid ${colors.borderGrey};
    }
`;
